import { useApolloClient } from "@apollo/client";
import { notNull } from "@msys/common";
import { useTolgee, useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { namedOperations } from "../../clients/graphqlTypes.js";
import { useUrlSearchParams } from "../commons/hooks/useUrlSearchParams.js";
import {
  ORGANISATION_TYPE_URL_SEARCH_PARAM,
  REF_TOKEN_URL_SEARCH_PARAM,
} from "../constants.js";
import { useCreateNewOrganisationAndUserMutation } from "./CreateNewOrganisationPage.generated.js";
import type { CreateableOrganisationType } from "./isValidOrganisationType.js";
import {
  getSignupRefToken,
  shouldRemoveSignupRefToken,
  type SignupTokenProps,
} from "./signupRefToken.js";

export interface CreateOrganisationAndUserInput extends Pick<
  SignupTokenProps,
  "referral" | "refToken"
> {
  organisationType: CreateableOrganisationType;
  title: string;
  /** The user's language; defaults to the app's current language. */
  locale?: string;
}

/**
 * The `createOrganisationAndUser` call every self-service signup path shares:
 * the organisation-creation page (form) and the automatic creation from the
 * signup claims.
 *
 * `createOrganisationAndUser` resolves with the id of the new user once the
 * available identities are refetched, after the signup URL parameters are
 * cleaned up; it rejects when the creation fails. `handleSignupError` tells
 * the visitor about such a failure.
 */
export function useCreateOrganisationAndUser() {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["PageSignup"]);
  const currentLocale = useTolgee(["language"]).getLanguage();
  const { setUrlSearchParams } = useUrlSearchParams();

  const client = useApolloClient();
  const [createNewOrganisationAndUser] =
    useCreateNewOrganisationAndUserMutation({
      client,
      awaitRefetchQueries: true,
      refetchQueries: [namedOperations.Query.AvailableUsers],
    });

  const createOrganisationAndUser = React.useCallback(
    async ({
      organisationType,
      title,
      referral,
      refToken,
      locale = currentLocale,
    }: CreateOrganisationAndUserInput) => {
      const result = await createNewOrganisationAndUser({
        variables: {
          input: {
            refToken: getSignupRefToken(refToken, referral?.token),
            organisation: {
              title,
              phones: [],
              organisationType,
            },
            user: {
              phones: [],
              locale,
            },
          },
        },
        context: {
          noErrorNotification: isAlreadyExistingEmailError,
        },
      });

      const user = result.data?.createOrganisationAndUser.user;

      if (!user) throw new Error(`user creation failed`);

      // The token stays in the URL until the backend has accepted it, so that a
      // reload after a failure can still use it.
      setUrlSearchParams(
        {},
        getSignupUrlSearchParamsToRemove(organisationType, refToken, referral)
      );
      return user.id;
    },
    [createNewOrganisationAndUser, currentLocale, setUrlSearchParams]
  );

  const handleSignupError = React.useCallback(
    (e: unknown) => {
      if (e instanceof Error) {
        if (isAlreadyExistingEmailError(e.message)) {
          enqueueSnackbar(
            t("Email address is already registered", {
              ns: "PageSignup",
            }),
            {
              variant: "error",
            }
          );
          return;
        }

        enqueueSnackbar(e.message, { variant: "error" });
        return;
      }
      throw new Error(`expecting an Error instance`);
    },
    [enqueueSnackbar, t]
  );

  return { createOrganisationAndUser, handleSignupError };
}

/**
 * A craftsman signup has always dropped the `refToken` parameter, a homeowner
 * signup only drops it when it was the CRM or referral token that was used.
 */
function getSignupUrlSearchParamsToRemove(
  organisationType: CreateableOrganisationType,
  refToken: SignupTokenProps["refToken"],
  referral: SignupTokenProps["referral"]
) {
  return [
    ORGANISATION_TYPE_URL_SEARCH_PARAM,
    organisationType === "CRAFTSMAN" ||
    shouldRemoveSignupRefToken(refToken, referral?.token)
      ? REF_TOKEN_URL_SEARCH_PARAM
      : null,
  ].filter(notNull);
}

const isAlreadyExistingEmailError = (message: string) =>
  message === "email already exists" ||
  message.includes(
    'duplicate key value violates unique constraint "uniq_system_users_email"'
  );
